interface EnvironmentConfig {
  /** Backend GraphQL API URL */
  apiBaseUrl: string;

  /** Backend WebSocket URL */
  wsUrl: string;

  /** Sentry DSN for error reporting (optional in development) */
  sentryDsn: string | null;

  /** Build mode */
  mode: "development" | "production";

  /** Whether the app is in development mode */
  isDev: boolean;

  /** Whether the app is in production mode */
  isProd: boolean;
}

function validateUrl(value: string, name: string): string {
  try {
    new URL(value);
    return value;
  } catch {
    throw new Error(`Invalid URL for ${name}: "${value}"`);
  }
}

function loadEnvironment(): EnvironmentConfig {
  const mode = import.meta.env.MODE as "development" | "production";
  const isDev = mode === "development";
  const isProd = mode === "production";

  const apiBaseUrl =
    import.meta.env.VITE_API_BASE_URL || (isDev ? "http://localhost:4000/api/graphql" : "");
  const wsUrl = import.meta.env.VITE_WS_URL || (isDev ? "ws://localhost:4000/api/graphql-ws" : "");
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN || null;

  // production builds must have required variables
  if (isProd) {
    const missing: string[] = [];
    if (!apiBaseUrl) missing.push("VITE_API_BASE_URL");
    if (!wsUrl) missing.push("VITE_WS_URL");
    if (!sentryDsn) missing.push("VITE_SENTRY_DSN");
    if (missing.length > 0) {
      throw new Error(
        `Missing required environment variables for production build: ${missing.join(", ")}`,
      );
    }
  }

  if (apiBaseUrl) validateUrl(apiBaseUrl, "VITE_API_BASE_URL");
  if (wsUrl) validateUrl(wsUrl, "VITE_WS_URL");

  return {
    apiBaseUrl,
    wsUrl,
    sentryDsn,
    mode,
    isDev,
    isProd,
  };
}

export const env: EnvironmentConfig = loadEnvironment();
